/** 
 * Represents the usage limits associated with a user's plan
 * @interface PlanLimits
 */
export interface PlanLimits {
  /** Number of remaining item scans available to the user */
  ai_scans_per_month: number;
  /** Maximum number of totes allowed under the plan */
  total_totes: number;
  /** Maximum number of workspaces allowed under the plan */
  total_workspaces: number;
  /** Maximum number of items that can be stored in a single tote */
  max_items_per_tote: number;
  /** Number of reports a month */
  max_reports: number;
  /** Max number of scheduled reports */
  max_scheduled_reports: number;
}

/**
 * Represents the current usage metrics of a user's plan
 * @interface PlanUsage
 */
export interface PlanUsage {
  /** Current number of totes being used */
  totes: number;
  /** Current number of workspaces being used */
  workspaces: number;
  /** Number of AI scans used this month */
  ai_scans_this_month: number;
  /** AI reports generated this month */
  reports_generated_this_month: number;
  /** Total scheduled reports */
  scheduled_reports: number;
}

/**
 * Combines plan limits and current usage information
 * @interface PlanOverview
 */
export interface PlanOverview {
  /** The plan's usage limits */
  limits: PlanLimits;
  /** Current usage metrics */
  usage: PlanUsage;
}

/**
 * Represents a user in the system
 * @interface User
 */
export interface User {
  /** Unique identifier for the user */
  id: number;
  /** User's email address */
  email: string;
  /** User's display name */
  name: string;
  /** Whether the user account is active */
  is_active: boolean;
  /** Whether the user has administrator privileges */
  is_admin: boolean;
  /** Whether the user's email has been confirmed */
  email_confirmed: boolean;
  /** ISO timestamp of when the user was created */
  created_at: string;
  /** ISO timestamp of when the user was last updated */
  updated_at: string;
  /** Google OAuth ID */
  google_id?: string;
  /** Facebook OAuth ID */
  facebook_id?: string;
  /** eBay OAuth ID */
  ebay_id?: string;
  /** OAuth data for connected accounts */
  oauth_data?: {
    google?: {
      sub?: string;
      name?: string;
      email?: string;
      picture?: string;
      tokens?: {
        access_token: string;
        refresh_token: string;
        token_type: string;
        expiry: number;
      };
    };
    facebook?: {
      id?: string;
      name?: string;
      email?: string;
      picture?: {
        data?: {
          url?: string;
        };
      };
    };
    ebay?: {
      userId?: string;
      username?: string;
      email?: string;
      status?: string;
      accountType?: string;
    };
  };
  /** When the account is scheduled to be deleted */
  scheduled_deletion?: string;
  /** When the scheduled deletion was last cancelled */
  deletion_cancelled_at?: string;
  /** Plan identifier */
  plan_identifier: string | null;
  /** Plan start date */
  plan_start_date: string | null;
  /** Plan bill date */
  plan_bill_date: string | null;
  /** Current billing price */
  current_billing_price: number | null;
  /** Number of rebill failures */
  rebill_failures: number;
  /** Next plan to change to at billing change */
  next_plan: string | null;
  /** Next plan price to change to at billing change */
  next_plan_price: number | null;
  /** Stripe customer ID */
  stripe_customer_id?: string;
  /** Stripe subscription ID */
  stripe_subscription_id?: string;
  /** Stripe payment method ID */
  stripe_payment_method_id?: string;
  /** Number of scans so far this month */
  ai_scans_this_month: number;
  /** Number of scans per month */
  ai_scans_per_month: number;
  /** Total number of totes allowed */
  total_totes: number;
  /** Total number of workspaces allowed */
  total_workspaces: number;
  /** Maximum items per tote */
  max_items_per_tote: number;
  /** Maximum number of reports per month */
  max_reports: number;
  /** Number of reports generated this month */
  reports_generated_this_month: number;
  /** Maximum number of scheduled reports */
  max_scheduled_reports: number;
}

/**
 * Represents a storage tote in the system
 * @interface Tote
 */
export interface Tote {
  /** Unique identifier for the tote */
  id: number;
  /** Display name of the tote */
  name: string;
  /** Optional description of the tote's contents or purpose */
  description?: string;
  /** Optional URL to a photo of the tote */
  photo_url?: string;
  /** Optional description of where the tote is located */
  location_description?: string;
  /** ISO timestamp of when the tote was created */
  created_at: string;
  /** ISO timestamp of when the tote was last updated */
  updated_at: string;
  /** ID of the workspace this tote belongs to, null for personal totes */
  workspace_id?: number | null;
  /** Current number of items stored in the tote */
  registered_items: number;
  /** Maximum number of items allowed in the tote (-1 for unlimited) */
  max_items: number;
  /** Custom fields for the tote */
  custom_fields?: Record<string, string>;
  /** Location data for the tote */
  location?: LocationData;
  /** User access level information */
  user_access?: {
    can_view: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_add_items: boolean;
  };
}

/**
 * Generic interface for paginated API responses
 * @interface PaginatedResponse
 * @template T The type of items being paginated
 */
export interface PaginatedResponse<T> {
  /** Array of items for the current page */
  items: T[];
  /** Total number of items across all pages */
  total: number;
  /** Current page number */
  page: number;
  /** Number of items per page */
  per_page: number;
  /** Total number of pages available */
  total_pages: number;
}

/**
 * Represents an item in the inventory system
 * @interface Item
 */
export interface ItemCreator {
  /** Unique identifier for the creator */
  id: number;
  /** Creator's name */
  name: string;
  /** Creator's email */
  email: string;
}

/**
 * Represents a document attached to an item
 * @interface Document
 */
export interface Document {
  /** Unique identifier for the document */
  id: string;
  /** Display name of the document */
  name: string;
  /** Optional description of the document */
  description?: string;
  /** Type of document: google_drive, url, or text */
  type: 'google_drive' | 'url' | 'text';
  /** URL for google_drive and url document types */
  url?: string;
  /** Content for text document type */
  content?: string;
}

export interface LocationData {
  latitude: number;
  longitude: number;
  source: 'geolocation' | 'exif' | 'manual';
  timestamp: string;
  description?: string;
}

export interface Item {
  /** Unique identifier for the item */
  id: number;
  /** Display name of the item */
  name: string;
  /** Optional description of the item */
  description?: string;
  /** Array of image URLs for the item */
  image_urls: string[];
  /** Map of keywords to their relevance scores */
  keywords: Record<string, number>;
  /** Quantity of this item in inventory */
  quantity: number;
  /** Optional unit for the quantity (e.g., kg, lb, oz) */
  quantity_unit?: string;
  /** Optional ID of the tote this item is stored in */
  tote_id?: number;
  /** Current status of the item */
  status: string;
  /** ISO timestamp of when the item was created */
  created_at: string;
  /** ISO timestamp of when the item was last updated */
  updated_at: string;
  /** Optional ID of the workspace this item belongs to */
  workspace_id?: number;
  /** Optional price of the item */
  price?: number | null;
  /** Optional package weight in ounces */
  weight_oz?: number | null;
  /** Optional package length in inches */
  package_length_in?: number | null;
  /** Optional package width in inches */
  package_width_in?: number | null;
  /** Optional package height in inches */
  package_height_in?: number | null;
  /** Blob Image URL */
  blob_image_url?: string | null;
  /** Item creator information */
  creator?: ItemCreator;
  /** Location data */
  location?: LocationData;
  /** Documents attached to the item */
  documents?: Array<Document>;
  /** Custom fields for the item */
  custom_fields?: Record<string, string>;
  /** User access level information */
  user_access?: {
    can_view: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_move: boolean;
  };
}

export interface ItemCreateData {
  name: string;
  description?: string;
  keywords: Record<string, number>;
  quantity?: number;
  tote_id: number;
  status?: string;
  workspace_id?: number;
  price?: number | null;
  weight_oz?: number | null;
  package_length_in?: number | null;
  package_width_in?: number | null;
  package_height_in?: number | null;
  location?: LocationData;
}

/**
 * Types of events that can occur in an item's history
 * @type ItemHistoryEventType
 */
export type ItemHistoryEventType = 
  | 'CREATED'
  | 'MODIFIED'
  | 'STATUS_CHANGED'
  | 'MOVED_TO_WORKSPACE'
  | 'MOVED_TO_TOTE'
  | 'KEYWORDS_MODIFIED'
  | 'DESCRIPTION_MODIFIED'
  | 'IMAGE_MODIFIED'
  | 'DOCUMENTS_MODIFIED';

/**
 * Represents a historical event in an item's timeline
 * @interface ItemHistory
 */
export interface ItemHistory {
  /** ISO timestamp of when the event occurred */
  timestamp: string;
  /** Name of the item at the time of the event */
  item_name: string;
  /** User who triggered the event */
  user_name: {
    name: string;
    email: string;
    id: number;
    is_admin: boolean;
    [key: string]: unknown;
  };
  /** Type of event that occurred */
  event_type: ItemHistoryEventType;
  /** Detailed information about the changes made */
  details: {
    /** Map of changed fields to their before/after values */
    changes?: Record<string, {
      before: string | number | boolean | null | Record<string, unknown> | Array<unknown>;
      after: string | number | boolean | null | Record<string, unknown> | Array<unknown>;
    }>;
    /** Complete initial state for creation events */
    initial_state?: {
      name: string;
      description?: string;
      quantity: number;
      price?: number;
      tote_id?: number;
      keywords: Record<string, number>;
      image_urls: string[];
      documents?: Array<{id: string; name: string; description: string}>;
      workspace_id?: number;
      status: string;
      custom_fields?: Record<string, string>;
      user_id: number;
      created_at: string;
      updated_at: string;
    };
    /** Complete state before modification */
    before?: {
      name: string;
      description?: string;
      quantity: number;
      price?: number;
      tote_id?: number;
      keywords: Record<string, number>;
      image_urls: string[];
      documents?: Array<{id: string; name: string; description: string}>;
      workspace_id?: number;
      status: string;
      custom_fields?: Record<string, string>;
    };
    /** Complete state after modification */
    after?: {
      name: string;
      description?: string;
      quantity: number;
      price?: number;
      tote_id?: number;
      keywords: Record<string, number>;
      image_urls: string[];
      documents?: Array<{id: string; name: string; description: string}>;
      workspace_id?: number;
      status: string;
      custom_fields?: Record<string, string>;
    };
    /** Complete final state for deletion events */
    final_state?: {
      name: string;
      description?: string;
      quantity: number;
      price?: number;
      tote_id?: number;
      keywords: Record<string, number>;
      image_urls: string[];
      documents?: Array<{id: string; name: string; description: string}>;
      workspace_id?: number;
      status: string;
      custom_fields?: Record<string, string>;
      user_id: number;
      created_at: string;
      updated_at: string;
    };
    /** Action for status changes */
    action?: 'deleted';
    /** Previous status before a status change */
    old_status?: string;
    /** New status after a status change */
    new_status?: string;
    /** Description for status changes */
    description?: string;
  };
}

/**
 * Represents a status change in an item's history
 * @interface ItemStatusHistory
 */
export interface ItemStatusHistory {
  /** Unique identifier for the status change event */
  id: number;
  /** Previous status before the change */
  old_status?: string;
  /** New status after the change */
  new_status: string;
  /** Optional description of why the status was changed */
  description?: string;
  /** ISO timestamp of when the status change occurred */
  created_at: string;
}

/**
 * Represents an error that occurred during item scanning
 * @interface ScanError
 */
export interface ScanError {
  /** Error message describing what went wrong */
  message: string;
  /** Severity level of the error */
  severity: 'error' | 'warning' | 'info';
}

/**
 * Represents a successful item scan
 * @interface ScanSuccess
 */
export interface ScanSuccess {
  /** The scanned item */
  item: Item;
  /** Optional suggested tote for storing the item */
  suggested_tote?: Tote;
  /** Confidence score of the item recognition */
  confidence_score: number;
  /** Keywords extracted from the scan */
  keywords: string[];
  /** Map of tote IDs to their relevance scores for this item */
  tote_scores: Record<number, number>;
}

/**
 * Represents a bounding box for an item in an image
 * @interface BoundingBox
 */
export interface BoundingBox {
  /** Left coordinate */
  x1: number;
  /** Top coordinate */
  y1: number;
  /** Right coordinate */
  x2: number;
  /** Bottom coordinate */
  y2: number;
}

/**
 * Represents a detected item in a multi-item scan
 * @interface DetectedItem
 * @extends Item
 */
export interface DetectedItem extends Item {
  /** Bounding box coordinates */
  bbox: BoundingBox;
  /** Detection confidence score */
  confidence: number;
  /** Optional suggested tote for storing the item */
  suggested_tote?: Tote;
  /** Map of tote IDs to their relevance scores for this item */
  tote_scores?: Record<number, number>;
}

/**
 * Represents a successful multi-item scan
 * @interface MultiItemScanSuccess
 */
export interface MultiItemScanSuccess {
  /** Array of detected items */
  items: DetectedItem[];
  /** URL to the original scanned image */
  original_image: string;
  /** Indicates this is a multi-item scan */
  is_multi_item: true;
}

/** Union type for scan responses */
export type ScanResponse = ScanSuccess | MultiItemScanSuccess | ScanError;

/**
 * Type guard to determine if a scan response is an error
 * @param response - The scan response to check
 * @returns True if the response is a ScanError, false otherwise
 */
export function isScanError(response: ScanResponse): response is ScanError {
  return 'severity' in response;
}

/**
 * Type guard to determine if a scan response is a multi-item scan
 * @param response - The scan response to check
 * @returns True if the response is a MultiItemScanSuccess, false otherwise
 */
export function isMultiItemScan(response: ScanResponse): response is MultiItemScanSuccess {
  return !isScanError(response) && 'is_multi_item' in response && response.is_multi_item === true;
}

/**
 * Enumeration of possible workspace access levels
 * @enum {string}
 */
export enum WorkspaceAccessLevel {
  /** Has full control over the workspace */
  OWNER = 'OWNER',
  /** Can manage workspace settings and members */
  ADMIN = 'ADMIN',
  /** Can manage workspace content */
  MANAGER = 'MANAGER',
  /** Can modify workspace content */
  MEMBER = 'MEMBER',
  /** Can only view workspace contents */
  VIEWER = 'VIEWER'
}

/**
 * Data required to create a new workspace member
 * @interface WorkspaceMemberCreate
 */
export interface WorkspaceMemberCreate {
  /** Email address of the user to add */
  email: string;
  /** Access level to grant to the user */
  access_level: WorkspaceAccessLevel;
}

/**
 * Represents a member of a workspace
 * @interface WorkspaceMember
 */
export interface WorkspaceMember {
  /** Unique identifier for the membership */
  id: number;
  /** Information about the user */
  user: {
    /** User's unique identifier */
    id: number;
    /** User's email address */
    email: string;
    /** User's display name */
    name: string;
  };
  /** Member's access level in the workspace */
  access_level: WorkspaceAccessLevel;
  /** ISO timestamp of when the member was added */
  created_at: string;
  /** ISO timestamp of when the member was last updated */
  updated_at: string;
}

/**
 * Represents a workspace in the system
 * @interface Workspace
 */
export interface Workspace {
  /** Unique identifier for the workspace */
  id: number;
  /** Display name of the workspace */
  name: string;
  /** Optional description of the workspace */
  description?: string;
  /** ISO timestamp of when the workspace was created */
  created_at: string;
  /** ISO timestamp of when the workspace was last updated */
  updated_at: string;
  /** List of workspace members */
  members: WorkspaceMember[];
  /** Current number of totes in the workspace */
  registered_totes: number;
  /** Maximum number of totes allowed (-1 for unlimited) */
  max_totes: number;
  /** Maximum number of users allowed in the workspace */
  max_users: number;
  /** Custom fields for the workspace */
  custom_fields?: Record<string, string>;
  /** Location data for the workspace */
  location?: LocationData;
  /** Whether to use workspace owner's AI scan credits for members */
  use_workspace_credits?: boolean;
  /** Whether to use workspace owner's report resources for scheduled reports */
  use_workspace_report_resources?: boolean;
  /** User access level information */
  user_access?: {
    can_view: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_manage_members: boolean;
    can_create_totes: boolean;
  };
}

/**
 * Response from Stripe subscription operations
 * @interface SubscriptionResponse
 */
export interface SubscriptionResponse {
  /** Stripe subscription ID */
  subscription_id: string;
  /** Current status of the subscription */
  status: string;
  /** Client secret for payment confirmation */
  client_secret?: string;
  /** Timestamp when current period ends */
  current_period_end?: number;
  /** Timestamp when subscription will be cancelled */
  cancel_at?: number;
  /** Whether subscription will cancel at period end */
  cancel_at_period_end?: boolean;
}
